import { useMemo } from "react";

import { SaaSConsoleType, TierPackageTypes } from "@doitintl/cmp-models";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";

import AWSLogoLight from "../../assets/aws-logo.svg";
import AWSLogoDark from "../../assets/aws-logo-dark-mode.svg";
import AzureLogo from "../../assets/azure-logo.svg";
import DatadogLogo from "../../assets/datadog-logo.png";
import DatadogLogoDarkMode from "../../assets/datadog-logo-white.png";
import GCPLogo from "../../assets/gcp-logo.svg";
import OpenaiLogo from "../../assets/openai-logo.svg";
import OpenaiLogoDarkMode from "../../assets/openai-logo-white.svg";
import SnowflakeLogo from "../../assets/snowflake-logo.svg";
import { homeTexts } from "../../assets/texts";
import { useShouldShowOnboardingCards } from "../../Components/Dashboard/CloudConnection/hooks";
import { useDarkThemeCheck } from "../../Components/hooks/useDarkThemeCheck";
import { useCustomerContext } from "../../Context/CustomerContext";
import { useTier } from "../../Context/TierProvider";
import { useRoutesConfig } from "../../Navigation/Components/hooks";
import { type PageId } from "../../Navigation/config/pages";
import { customersRoute } from "../../Navigation/core/utils";
import { isDCIEssentialsTier, isHeritageTier } from "../../Pages/Settings/General/utils";
import { CloudConnectCard } from "./CloudConnect/CloudConnectCard";
import SectionHeader from "./SectionHeader";

type Route = {
  pageId: PageId;
  logo: string;
  path: string;
  cloudType: SaaSConsoleType;
};

export const CloudConnect = () => {
  const routesConfig = useRoutesConfig();
  const { customer } = useCustomerContext();
  const { getCustomerTier } = useTier();
  const navigatorTier = getCustomerTier(TierPackageTypes.NAVIGATOR);

  const theme = useTheme();
  const {
    palette: { mode },
  } = theme;
  const isDarkMode = useDarkThemeCheck();

  const {
    shouldShowNavigatorWelcomeBlock,
    shouldShowSolveWelcomeBlock,
    shouldShowAzureCloudConnect,
    shouldShowBillingProfileCard,
    shouldShowBillingProfileCardForHybridCustomer,
    shouldShowStandaloneCloudSelector,
    shouldShowSaasCloudSelector,
    shouldShowWelcomeBlock,
    shouldShowHeritageCloudConnect,
    shouldShowSnowflakeOnboardingCard,
    shouldShowDatadogOnboardingCard,
    shouldShowOpenaiOnboardingCard,
  } = useShouldShowOnboardingCards();

  const allowedRoutes = useMemo(() => {
    const routes: Route[] = [
      {
        pageId: "saas:onboarding:aws",
        logo: mode === "dark" ? AWSLogoDark : AWSLogoLight,
        cloudType: SaaSConsoleType.AWS,
        path: "cloud-onboarding-aws",
      },
      {
        pageId: "saas:onboarding:gcp",
        logo: GCPLogo,
        cloudType: SaaSConsoleType.GCP,
        path: "cloud-onboarding-gcp",
      },
      {
        pageId: "saas:onboarding:azure",
        logo: AzureLogo,
        cloudType: SaaSConsoleType.AZURE,
        path: "cloud-onboarding-azure",
      },
    ];

    if (shouldShowSnowflakeOnboardingCard) {
      routes.push({
        pageId: "saas:onboarding:snowflake",
        logo: SnowflakeLogo,
        cloudType: SaaSConsoleType.SNOWFLAKE,
        path: "cloud-onboarding-snowflake",
      });
    }

    if (shouldShowDatadogOnboardingCard) {
      routes.push({
        pageId: "saas:onboarding:datadog",
        logo: isDarkMode ? DatadogLogoDarkMode : DatadogLogo,
        cloudType: SaaSConsoleType.DATADOG,
        path: "cloud-onboarding-datadog",
      });
    }

    if (shouldShowOpenaiOnboardingCard) {
      routes.push({
        pageId: "saas:onboarding:openai",
        logo: isDarkMode ? OpenaiLogoDarkMode : OpenaiLogo,
        cloudType: SaaSConsoleType.OPENAI,
        path: "cloud-onboarding-openai",
      });
    }

    return routes.filter((r) => routesConfig?.find((conf) => conf.pageId === r.pageId));
  }, [
    isDarkMode,
    mode,
    routesConfig,
    shouldShowDatadogOnboardingCard,
    shouldShowOpenaiOnboardingCard,
    shouldShowSnowflakeOnboardingCard,
  ]);

  if (
    shouldShowAzureCloudConnect ||
    shouldShowBillingProfileCard ||
    shouldShowBillingProfileCardForHybridCustomer ||
    shouldShowNavigatorWelcomeBlock ||
    shouldShowSolveWelcomeBlock ||
    shouldShowStandaloneCloudSelector ||
    shouldShowSaasCloudSelector ||
    shouldShowWelcomeBlock ||
    shouldShowHeritageCloudConnect ||
    !allowedRoutes.length ||
    isHeritageTier(navigatorTier) ||
    isDCIEssentialsTier(navigatorTier)
  ) {
    return null;
  }

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <SectionHeader text={homeTexts.CLOUD_CONNECT_TITLE} />
      </Grid>
      {allowedRoutes.map((c) => (
        <Grid
          key={c.pageId}
          size={{
            xs: 12,
            sm: 12,
            md: 4,
          }}
        >
          <CloudConnectCard logo={c.logo} cloudType={c.cloudType} route={customersRoute(customer.id, c.path)} />
        </Grid>
      ))}
    </Grid>
  );
};
